import { useMemo } from 'react';

// Import types
import type { EventByUser } from '../../types/schedule';

export type AvailabilityLegendAndHoverProps = {
  users: string[]; // userIds
  userIdToName?: Record<string, string>;
  eventsByUser: EventByUser;
  hoverCell: { dateKey: string; row: number } | null;
  getUsersAtCell: (dateKey: string, row: number) => string[]; // returns userIds
  isOverlapTimes?: boolean;
};

/**
 * Renders the legend for availability/overlapping-time colors and a single-line
 * "Available / Unavailable" summary for the currently hovered cell.
 *
 * This component is reusable across editing and overlapping-times modes. It relies on
 * the parent to provide the current hover cell and a function to get users who
 * are available at a given cell.
 */
export default function AvailabilityLegendAndHover(
  props: AvailabilityLegendAndHoverProps,
) {
  const {
    users,
    userIdToName = {},
    eventsByUser,
    hoverCell,
    getUsersAtCell,
    isOverlapTimes,
  } = props;

  // Participants who provided any availability at all
  const eligible = useMemo(
    () => users.filter((id) => (eventsByUser[id] || []).length > 0),
    [users, eventsByUser],
  );

  // Compute hover summary strings
  const summary = useMemo(() => {
    if (!hoverCell || eligible.length === 0)
      return {
        availText: 'Available: —',
        unavailText: 'Unavailable: —',
      };
    const availIds = getUsersAtCell(hoverCell.dateKey, hoverCell.row).filter(
      (id) => eligible.includes(id),
    );
    const unavailIds = eligible.filter((id) => !availIds.includes(id));

    const toName = (id: string) => userIdToName[id] || id;
    const availText =
      availIds.length > 0
        ? `Available: ${availIds.map(toName).join(', ')}`
        : 'Available: —';
    const unavailText =
      unavailIds.length > 0
        ? `Unavailable: ${unavailIds.map(toName).join(', ')}`
        : 'Unavailable: —';
    return { availText, unavailText };
  }, [hoverCell, eligible, getUsersAtCell, userIdToName]);

  return (
    <div className="flex flex-col items-center text-xs text-gray-700 gap-2">
      {/* Legend */}
      <div className="flex flex-wrap justify-center items-center gap-3">
        <span className="text-sm font-bold text-gray-600">Legend:</span>
        {isOverlapTimes ? (
          <>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-amber-100 border border-amber-200 inline-block" />
              <span className="text-sm font-semibold">
                No Overlap (1 available)
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-emerald-100 border border-emerald-200 inline-block" />
              <span className="text-sm font-semibold">
                Partial Overlap (2+ available)
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-emerald-400 border border-emerald-500 inline-block" />
              <span className="text-sm font-semibold">
                Full Overlap (Everyone)
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-emerald-100 border border-emerald-200 inline-block" />
              <span className="text-sm font-semibold">Your availability</span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-gray-100 border border-gray-200 inline-block" />
              <span className="text-sm font-semibold">Others available</span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="w-4 h-3 rounded bg-white border border-gray-200 inline-block" />
              <span className="text-sm font-semibold">No availability</span>
            </div>
          </>
        )}
      </div>

      <div className="flex text-sm text-gray-600">
        Mouse over the calendar cells to see who is available.
      </div>

      {/* Single-line who-is-available summary (instant on hover) */}
      <div className="flex flex-col md:flex-row text-sm text-gray-800 font-medium px-2">
        <span className="text-sm font-semibold">{summary.availText}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span className="text-sm font-semibold">{summary.unavailText}</span>
      </div>
    </div>
  );
}
